import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

const { FiCalendar, FiClock, FiMapPin, FiUser, FiFileText, FiCheckCircle } = FiIcons;

const Appointments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [providers, setProviders] = useState([]);
  const [formData, setFormData] = useState({
  service_request: "",
  service_type: "",
  assign_provider: "no",
  provider_id: "",
  title: "",
  description: "",
  start_time: "",
  duration: 30,
  location: "",
  phone: "",
  email: "",
});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Redirect if no user
  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  // Fetch service requests and providers
  useEffect(() => {
    const fetchData = async () => {
  setLoading(true);

  try {
    const { data: providersData, error: provError } = await supabase
      .from("providers")
      .select("*")
      .eq("is_active", true)
      .order("full_name");

    if (provError) throw provError;

    console.log("Providers:", providersData);

    setProviders(providersData || []);
  } catch (err) {
    console.error("Error fetching data:", err);
  } finally {
    setLoading(false);
  }
};

    if (user) fetchData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccessMsg(null);

    if (
  !formData.service_request ||
  !formData.service_type ||
  !formData.title ||
  !formData.start_time
) {
      setError("Please fill in all required fields.");
      setSubmitting(false);
      return;
    }

    try {
      const { data, error } = await supabase.from("appointments").insert([
        {
  user_id: user.id,
  service_request: formData.service_request,
  provider_id:
    formData.assign_provider === "yes"
      ? formData.provider_id
      : null,
  service_type: formData.service_type,
  title: formData.title,
  description: formData.description,
  start_time: new Date(formData.start_time).toISOString(),
  duration: parseInt(formData.duration, 10),
  location: formData.location,
  phone: formData.phone,
  email: formData.email,
  status: "pending",
},
      ]);

      if (error) throw error;

      setSuccessMsg("Appointment created successfully!");
      setFormData({
  service_request: "",
  service_type: "",
  assign_provider: "no",
  provider_id: "",
  title: "",
  description: "",
  start_time: "",
  duration: 30,
  location: "",
  phone: "",
  email: "",
});
    } catch (err) {
      console.error("Error creating appointment:", err);
      setError(err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading appointment data...</p>
        </div>
      </div>
    );
  }

  // Time slots array
  const timeSlots = ["09:00","09:30","10:00","10:30","11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30"];

  const filteredProviders = providers.filter(
  (provider) =>
    provider.service_type === formData.service_type &&
    provider.is_active
);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl mb-4 shadow-lg">
            <SafeIcon icon={FiCalendar} className="text-3xl text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Schedule Appointment</h1>
          <p className="text-gray-600 text-lg">Book your service appointment with ease</p>
        </motion.div>

        {/* Alert Messages */}
        {error && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
            </div>
          </motion.div>
        )}

        {successMsg && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
            <div className="flex items-center">
              <SafeIcon icon={FiCheckCircle} className="text-xl text-green-400 mr-3" />
              <p className="text-sm font-medium text-green-800">{successMsg}</p>
              <a href="/dashboard" className="text-sm font-medium text-green-800 ml-2 hover:underline">Go to Dashboard</a>
            </div>
          </motion.div>
        )}

        {/* Form Card */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <h2 className="text-2xl font-bold text-white">Appointment Details</h2>
            <p className="text-blue-100 mt-1">Fill in the information below to create your appointment</p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Service Request */}
<div>
  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
    <SafeIcon icon={FiFileText} className="mr-2 text-blue-600" />
    Service Request <span className="text-red-500 ml-1">*</span>
  </label>

  <input
    type="text"
    name="service_request"
    value={formData.service_request}
    onChange={handleChange}
    placeholder="e.g. Need property maintenance for my apartment"
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
    required
  />
</div>
{/* Service Type */}
<div>
  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
    <SafeIcon icon={FiCalendar} className="mr-2 text-blue-600" />
    Service Type <span className="text-red-500 ml-1">*</span>
  </label>

  <select
    name="service_type"
    value={formData.service_type}
    onChange={handleChange}
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
    required
  >
    <option value="">Select service type</option>

    <option value="property-care">
      🏠 Property Care
    </option>

    <option value="parental-care">
      👨‍👩‍👧 Parental Care
    </option>

    <option value="travel-services">
      ✈️ Travel Services
    </option>

    <option value="celebration-services">
      🎉 Celebration Services
    </option>

    <option value="financial-services">
      💰 Financial Services
    </option>
  </select>
</div>

{/* Assign Provider? */}
<div>
  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
    <SafeIcon icon={FiUser} className="mr-2 text-blue-600" />
    Assign Provider
  </label>

  <select
    name="assign_provider"
    value={formData.assign_provider}
    onChange={handleChange}
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
  >
    <option value="no">No</option>
    <option value="yes">Yes</option>
  </select>
</div>

{/* Provider Selection */}
{formData.assign_provider === "yes" && (
  <div>
    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
      <SafeIcon icon={FiUser} className="mr-2 text-blue-600" />
      Select Provider
    </label>

    <select
      name="provider_id"
      value={formData.provider_id}
      onChange={handleChange}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
    >
      <option value="">Choose Provider</option>

      {filteredProviders.map((provider) => (
        <option key={provider.id} value={provider.id}>
          {provider.full_name}
        </option>
      ))}
    </select>
  </div>
)}

            {/* Title */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <SafeIcon icon={FiFileText} className="mr-2 text-blue-600" />
                Appointment Title <span className="text-red-500 ml-1">*</span>
              </label>
              <input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" required placeholder="e.g., Home Inspection Visit" />
            </div>

            {/* Description */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <SafeIcon icon={FiFileText} className="mr-2 text-blue-600" />
                Description
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none" rows={4} placeholder="Add any additional details or special instructions..." />
            </div>

            {/* Start Time with Slots */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <SafeIcon icon={FiClock} className="mr-2 text-blue-600" />
                Start Time <span className="text-red-500 ml-1">*</span>
              </label>

              {/* Date Picker */}
              <input
                type="date"
                name="start_date"
                value={formData.start_time ? formData.start_time.slice(0, 10) : ""}
                onChange={(e) => {
                  const date = e.target.value;
                  if (!date) return;
                  const newDateTime = new Date(date + "T09:00");
                  setFormData((prev) => ({
                    ...prev,
                    start_time: newDateTime.toISOString().slice(0, 16),
                  }));
                }}
                className="w-full mb-2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                required
              />

              {/* Time Slots */}
              <div className="grid grid-cols-6 gap-2">
                {timeSlots.map((slot) => {
                  const datePart = formData.start_time ? formData.start_time.slice(0, 10) : new Date().toISOString().slice(0, 10);
                  const slotISO = new Date(`${datePart}T${slot}`).toISOString().slice(0, 16);
                  const isSelected = formData.start_time === slotISO;

                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, start_time: slotISO }))}
                      className={`px-2 py-1 text-sm rounded-lg border transition-all ${
                        isSelected
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-blue-50"
                      }`}
                    >
                      {slot}
                    </button>
                  );
                })}
              </div>

              {/* Hidden datetime-local for form submission */}
              <input type="datetime-local" name="start_time_hidden" value={formData.start_time} readOnly className="hidden" />
            </div>

            {/* Duration */}
            

            {/* Location */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                <SafeIcon icon={FiMapPin} className="mr-2 text-blue-600" />
                Location
              </label>
              <input type="text" name="location" value={formData.location} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" placeholder="Enter appointment location" />
            </div>

            {/* Phone */}
<div>
  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
    <SafeIcon icon={FiUser} className="mr-2 text-blue-600" />
    Phone Number
    <span className="text-red-500 ml-1">*</span>
  </label>

  <input
    type="tel"
    name="phone"
    value={formData.phone}
    onChange={handleChange}
    required
    placeholder="+91 9876543210"
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
  />
</div>

{/* Email */}
<div>
  <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
    <SafeIcon icon={FiUser} className="mr-2 text-blue-600" />
    Email Address
    <span className="text-red-500 ml-1">*</span>
  </label>

  <input
    type="email"
    name="email"
    value={formData.email}
    onChange={handleChange}
    required
    placeholder="example@email.com"
    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
  />
</div>

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Creating Appointment...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiCheckCircle} className="text-xl" />
                    <span>Create Appointment</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Appointments;
